import { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { trackEvent } from '@/lib/faro';
import { formatCurrency } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';

interface WheelSegment {
    id: number;
    label: string;
    type: 'fee' | 'tax' | 'payment_credit' | 'nothing' | 'custom';
    amount: string;
    color: string | null;
    weight: number;
}

interface Wheel {
    id: number;
    name: string;
    is_active: boolean;
    cooldown_minutes: number;
    last_spun_at: string | null;
    segments: WheelSegment[];
}

interface SpinResult {
    spin: { id: number; segment: WheelSegment };
    result: { segment: WheelSegment; effect: string; amount: string };
}

const SEGMENT_COLORS = ['#ef4444', '#3b82f6', '#22c55e', '#eab308', '#a855f7', '#f97316', '#06b6d4', '#ec4899'];

const TYPE_LABELS: Record<string, string> = {
    fee: 'Fee',
    tax: 'Tax',
    payment_credit: 'Credit',
    nothing: 'Nothing',
    custom: 'Custom',
};

const CX = 150;
const CY = 150;
const R = 140;

function buildPath(startDeg: number, endDeg: number) {
    const rad = (d: number) => ((d - 90) * Math.PI) / 180;
    const x1 = CX + R * Math.cos(rad(startDeg));
    const y1 = CY + R * Math.sin(rad(startDeg));
    const x2 = CX + R * Math.cos(rad(endDeg));
    const y2 = CY + R * Math.sin(rad(endDeg));
    const large = endDeg - startDeg > 180 ? 1 : 0;
    return `M ${CX} ${CY} L ${x1} ${y1} A ${R} ${R} 0 ${large} 1 ${x2} ${y2} Z`;
}

function SpinningWheel({ segments, rotation }: { segments: WheelSegment[]; rotation: number }) {
    const total = segments.reduce((s, seg) => s + seg.weight, 0);
    let cum = 0;

    return (
        <div className="relative mx-auto" style={{ maxWidth: 300 }}>
            {/* Pointer */}
            <div
                className="absolute top-0 left-1/2 z-10 -translate-x-1/2 -translate-y-1"
                style={{
                    width: 0,
                    height: 0,
                    borderLeft: '12px solid transparent',
                    borderRight: '12px solid transparent',
                    borderTop: '24px solid #f1f5f9',
                    filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.5))',
                }}
            />
            <svg
                viewBox="0 0 300 300"
                width="100%"
                style={{
                    transform: `rotate(${rotation}deg)`,
                    transition: 'transform 4s cubic-bezier(0.25, 0.1, 0.1, 1)',
                }}
            >
                {segments.map((seg, i) => {
                    const segDeg = (seg.weight / total) * 360;
                    const startDeg = cum;
                    const midDeg = cum + segDeg / 2;
                    cum += segDeg;
                    const endDeg = cum;
                    const color = seg.color || SEGMENT_COLORS[i % SEGMENT_COLORS.length];
                    const textR = R * 0.65;
                    const textRad = ((midDeg - 90) * Math.PI) / 180;
                    const tx = CX + textR * Math.cos(textRad);
                    const ty = CY + textR * Math.sin(textRad);

                    return (
                        <g key={seg.id}>
                            <path
                                d={buildPath(startDeg, endDeg)}
                                fill={color}
                                stroke="#0f172a"
                                strokeWidth="1.5"
                            />
                            {segDeg >= 14 && (
                                <text
                                    transform={`translate(${tx},${ty}) rotate(${midDeg})`}
                                    textAnchor="middle"
                                    dominantBaseline="middle"
                                    fill="white"
                                    fontSize={segDeg > 45 ? 13 : segDeg > 25 ? 10 : 8}
                                    fontWeight="600"
                                    style={{ paintOrder: 'stroke', stroke: 'rgba(0,0,0,0.6)', strokeWidth: 3 }}
                                >
                                    {seg.label.length > 10 ? seg.label.slice(0, 9) + '…' : seg.label}
                                </text>
                            )}
                        </g>
                    );
                })}
                <circle cx={CX} cy={CY} r="16" fill="#1e293b" stroke="#64748b" strokeWidth="2" />
                <circle cx={CX} cy={CY} r="5" fill="#94a3b8" />
            </svg>
        </div>
    );
}

export function WheelOfFortune({ contractId, isDom }: { contractId: number; isDom: boolean }) {
    const queryClient = useQueryClient();
    const [showCreate, setShowCreate] = useState(false);
    const [spinResult, setSpinResult] = useState<{ wheelId: number; result: SpinResult } | null>(null);
    const [spinning, setSpinning] = useState(false);
    // Per-wheel accumulated rotation degrees
    const rotationRef = useRef<Map<number, number>>(new Map());
    const [rotations, setRotations] = useState<Map<number, number>>(new Map());
    const [newWheel, setNewWheel] = useState({
        name: '',
        cooldown_minutes: '60',
        segments: [
            { label: '', type: 'fee' as const, amount: '', color: '', weight: '1' },
            { label: '', type: 'nothing' as const, amount: '0', color: '', weight: '1' },
        ],
    });

    const { data: wheelsData } = useQuery({
        queryKey: ['wheels', contractId],
        queryFn: () => api.get<{ data: Wheel[] }>(`/contracts/${contractId}/wheels`),
    });

    const createMutation = useMutation({
        mutationFn: (data: unknown) => api.post(`/contracts/${contractId}/wheels`, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['wheels', contractId] });
            setShowCreate(false);
            setNewWheel({
                name: '',
                cooldown_minutes: '60',
                segments: [
                    { label: '', type: 'fee', amount: '', color: '', weight: '1' },
                    { label: '', type: 'nothing', amount: '0', color: '', weight: '1' },
                ],
            });
        },
    });

    const spinMutation = useMutation({
        mutationFn: ({ wheelId }: { wheelId: number }) =>
            api.post<{ data: SpinResult }>(`/contracts/${contractId}/wheels/${wheelId}/spin`, {}),
        onSuccess: (res, { wheelId }) => {
            trackEvent('wheel_spun', { contract_id: contractId, effect: res.data.result.effect });
            const result = res.data;
            const wheel = wheels.find(w => w.id === wheelId);
            if (wheel) {
                const total = wheel.segments.reduce((s, seg) => s + seg.weight, 0);
                let cum = 0;
                let winMid = 0;
                for (const seg of wheel.segments) {
                    const segDeg = (seg.weight / total) * 360;
                    if (seg.id === result.result.segment.id) {
                        winMid = cum + segDeg / 2;
                        break;
                    }
                    cum += segDeg;
                }
                // Target: put winMid at the pointer (top = 0°)
                // After rotating R degrees clockwise, the point originally at (360-R)%360 is at top.
                // We want (360-R)%360 = winMid → R = (360-winMid)%360
                const target = (360 - winMid + 360) % 360;
                const current = rotationRef.current.get(wheelId) ?? 0;
                const currentMod = ((current % 360) + 360) % 360;
                const diff = ((target - currentMod) + 360) % 360;
                const newRot = current + 360 * 8 + diff;

                // Use a frame delay so the CSS transition sees a state change
                requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                        rotationRef.current.set(wheelId, newRot);
                        setRotations(new Map(rotationRef.current));
                    });
                });
            }

            setTimeout(() => {
                setSpinResult({ wheelId, result });
                setSpinning(false);
            }, 4500);

            queryClient.invalidateQueries({ queryKey: ['wheels', contractId] });
            queryClient.invalidateQueries({ queryKey: ['contract', String(contractId)] });
            queryClient.invalidateQueries({ queryKey: ['ledger', String(contractId)] });
            queryClient.invalidateQueries({ queryKey: ['contracts'] });
        },
        onError: () => setSpinning(false),
    });

    const deleteMutation = useMutation({
        mutationFn: (wheelId: number) => api.delete(`/contracts/${contractId}/wheels/${wheelId}`),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['wheels', contractId] }),
    });

    const wheels = wheelsData?.data ?? [];

    const handleSpin = (wheelId: number) => {
        setSpinning(true);
        setSpinResult(null);
        spinMutation.mutate({ wheelId });
    };

    const addSegment = () => {
        setNewWheel(w => ({
            ...w,
            segments: [...w.segments, { label: '', type: 'fee' as const, amount: '', color: '', weight: '1' }],
        }));
    };

    const updateSegment = (idx: number, field: string, value: string) => {
        setNewWheel(w => ({
            ...w,
            segments: w.segments.map((s, i) => i === idx ? { ...s, [field]: value } : s),
        }));
    };

    const removeSegment = (idx: number) => {
        if (newWheel.segments.length <= 2) return;
        setNewWheel(w => ({
            ...w,
            segments: w.segments.filter((_, i) => i !== idx),
        }));
    };

    return (
        <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-base">Wheel of Fortune</CardTitle>
                {isDom && (
                    <Button size="sm" variant="outline" onClick={() => setShowCreate(!showCreate)}>
                        {showCreate ? 'Cancel' : '+ Add Wheel'}
                    </Button>
                )}
            </CardHeader>
            <CardContent className="space-y-4">
                {showCreate && (
                    <div className="space-y-3 rounded border border-slate-700 p-3">
                        <div>
                            <label className="mb-1 block text-xs text-slate-400">Wheel Name</label>
                            <Input
                                placeholder="e.g. Drain Wheel"
                                value={newWheel.name}
                                onChange={e => setNewWheel(w => ({ ...w, name: e.target.value }))}
                            />
                        </div>
                        <div>
                            <label className="text-xs text-slate-400">Cooldown (minutes)</label>
                            <Input
                                type="number"
                                min="0"
                                value={newWheel.cooldown_minutes}
                                onChange={e => setNewWheel(w => ({ ...w, cooldown_minutes: e.target.value }))}
                            />
                        </div>
                        <div className="space-y-2">
                            <div className="flex items-center justify-between">
                                <span className="text-xs font-medium text-slate-300">Segments</span>
                                <Button size="sm" variant="ghost" onClick={addSegment}>+ Segment</Button>
                            </div>
                            {newWheel.segments.map((seg, idx) => (
                                <div key={idx} className="space-y-1.5 rounded border border-slate-800 p-2">
                                    <div className="flex gap-2">
                                        <Input
                                            placeholder="Segment label (shown on wheel)"
                                            value={seg.label}
                                            onChange={e => updateSegment(idx, 'label', e.target.value)}
                                        />
                                        <Button
                                            size="sm"
                                            variant="ghost"
                                            className="shrink-0 text-red-400"
                                            onClick={() => removeSegment(idx)}
                                            disabled={newWheel.segments.length <= 2}
                                        >
                                            ✕
                                        </Button>
                                    </div>
                                    <div className="grid grid-cols-3 gap-2">
                                        <div>
                                            <label className="mb-1 block text-[10px] text-slate-500">Type</label>
                                            <select
                                                className="w-full rounded border border-slate-700 bg-slate-900 px-2 py-1.5 text-xs text-slate-200"
                                                value={seg.type}
                                                onChange={e => updateSegment(idx, 'type', e.target.value)}
                                            >
                                                <option value="fee">Fee</option>
                                                <option value="tax">Tax</option>
                                                <option value="payment_credit">Credit</option>
                                                <option value="nothing">Nothing</option>
                                                <option value="custom">Custom</option>
                                            </select>
                                        </div>
                                        <div>
                                            <label className="mb-1 block text-[10px] text-slate-500">Amount ($)</label>
                                            <Input
                                                type="number"
                                                min="0"
                                                step="0.01"
                                                value={seg.amount}
                                                onChange={e => updateSegment(idx, 'amount', e.target.value)}
                                            />
                                        </div>
                                        <div>
                                            <label className="mb-1 block text-[10px] text-slate-500">Weight</label>
                                            <Input
                                                type="number"
                                                min="1"
                                                max="100"
                                                value={seg.weight}
                                                onChange={e => updateSegment(idx, 'weight', e.target.value)}
                                            />
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                        {createMutation.isError && (
                            <p className="text-sm text-red-400">
                                {(createMutation.error as any)?.message || 'Failed to create wheel.'}
                            </p>
                        )}
                        <Button
                            size="sm"
                            disabled={!newWheel.name || newWheel.segments.some(s => !s.label) || createMutation.isPending}
                            onClick={() => createMutation.mutate({
                                name: newWheel.name,
                                cooldown_minutes: parseInt(newWheel.cooldown_minutes) || 0,
                                segments: newWheel.segments.map(s => ({
                                    label: s.label,
                                    type: s.type,
                                    amount: parseFloat(s.amount) || 0,
                                    weight: parseInt(s.weight) || 1,
                                })),
                            })}
                        >
                            {createMutation.isPending ? 'Creating…' : 'Create Wheel'}
                        </Button>
                    </div>
                )}

                {wheels.length === 0 && !showCreate && (
                    <p className="text-sm text-slate-500 italic">No wheels configured.</p>
                )}

                {wheels.map(wheel => (
                    <div key={wheel.id} className="rounded border border-slate-700 p-3 space-y-3">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <span className="font-medium text-sm">{wheel.name}</span>
                                <Badge variant={wheel.is_active ? 'default' : 'secondary'}>
                                    {wheel.is_active ? 'Active' : 'Inactive'}
                                </Badge>
                            </div>
                            {isDom && (
                                <Button
                                    size="sm"
                                    variant="ghost"
                                    className="text-red-400 text-xs"
                                    onClick={() => deleteMutation.mutate(wheel.id)}
                                >
                                    Delete
                                </Button>
                            )}
                        </div>

                        {wheel.segments.length > 0 && (
                            <SpinningWheel
                                segments={wheel.segments}
                                rotation={rotations.get(wheel.id) ?? 0}
                            />
                        )}

                        {/* Segment legend */}
                        <div className="flex flex-wrap gap-1.5">
                            {wheel.segments.map((seg, i) => (
                                <div
                                    key={seg.id}
                                    className="flex items-center gap-1 rounded px-2 py-0.5 text-xs"
                                    style={{ backgroundColor: (seg.color || SEGMENT_COLORS[i % SEGMENT_COLORS.length]) + '30' }}
                                >
                                    <span
                                        className="h-2 w-2 shrink-0 rounded-full"
                                        style={{ backgroundColor: seg.color || SEGMENT_COLORS[i % SEGMENT_COLORS.length] }}
                                    />
                                    <span>{seg.label}</span>
                                    <span className="text-slate-400">
                                        ({TYPE_LABELS[seg.type]}{seg.type !== 'nothing' && ` ${formatCurrency(parseFloat(seg.amount))}`})
                                    </span>
                                </div>
                            ))}
                        </div>

                        <div className="flex items-center gap-3">
                            <Button
                                size="sm"
                                variant="outline"
                                className="border-purple-800 text-purple-400 hover:bg-purple-900/30"
                                disabled={spinning || !wheel.is_active}
                                onClick={() => handleSpin(wheel.id)}
                            >
                                {spinning && spinMutation.variables?.wheelId === wheel.id ? 'Spinning…' : 'Spin!'}
                            </Button>
                            {wheel.cooldown_minutes > 0 && (
                                <span className="text-xs text-slate-500">Cooldown: {wheel.cooldown_minutes}m</span>
                            )}
                        </div>

                        {spinMutation.isError && spinMutation.variables?.wheelId === wheel.id && (
                            <p className="text-sm text-red-400">
                                {(spinMutation.error as any)?.message || 'Spin failed.'}
                            </p>
                        )}

                        {spinResult?.wheelId === wheel.id && (
                            <div className="rounded bg-slate-800 p-3 text-center">
                                <p className="text-lg font-bold">{spinResult.result.result.segment.label}</p>
                                <p className="text-sm text-slate-400">
                                    {spinResult.result.result.effect === 'nothing'
                                        ? 'No effect!'
                                        : spinResult.result.result.effect === 'payment_credit'
                                            ? `Credit: ${formatCurrency(parseFloat(spinResult.result.result.amount))}`
                                            : `${TYPE_LABELS[spinResult.result.result.effect] ?? 'Charge'}: +${formatCurrency(parseFloat(spinResult.result.result.amount))}`}
                                </p>
                            </div>
                        )}
                    </div>
                ))}
            </CardContent>
        </Card>
    );
}
